import dayjs from "dayjs";
import { toDateOnly } from "../../utils/DateOnly";
import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import {
  Allocation,
  CreateBizDevAllocationPayload,
  CreateRosterStaffPayload,
  CreateTaskAllocationPayload,
  RosterStaffMember,
  RotaSlot,
  StaffWeeklyHours,
  SaveRotaSlotPayload,
  UpdateRosterStaffPayload,
} from "../../model/members/Rota";

function normalizeAllocation(raw: Record<string, unknown>): Allocation {
  return {
    kind: (raw.kind as Allocation["kind"]) ?? "Task",
    id: Number(raw.id ?? 0),
    staffId: Number(raw.staffId ?? 0),
    staffName: String(raw.staffName ?? ""),
    date: dayjs(raw.date as string).format("YYYY-MM-DD"),
    title: raw.title == null ? null : String(raw.title),
    focusArea: raw.focusArea == null ? null : String(raw.focusArea),
    target: raw.target == null ? null : String(raw.target),
    memberName: raw.memberName == null ? null : String(raw.memberName),
    membershipId: raw.membershipId == null ? null : String(raw.membershipId),
    brief: raw.brief == null ? null : String(raw.brief),
    // FORT-374. Left as a raw string/Date rather than normalised to a format here: the
    // completed-work view formats it for display, and the outstanding-work reads never
    // return a non-null value anyway.
    completedAt: (raw.completedAt as string | Date | null) ?? null,
    completedByStaffName: raw.completedByStaffName == null ? null : String(raw.completedByStaffName),
  };
}

// A failed GET resolves to undefined rather than rejecting unless `showError` is set —
// but `showError` also pops the client's own notification. The rota page renders load
// failures as an inline Alert, so it wants the rejection without the second surface:
// treat undefined as the failure signal and raise our own error.
function required<T>(raw: T | undefined, what: string): T {
  if (raw === undefined) throw new Error(`Failed to load ${what}.`);
  return raw;
}

export class RotaService extends ClubBookingApiClient {
  /**
   * The weekly pattern. With `weekStart` (the Monday, "YYYY-MM-DD") the response carries
   * that week's cover overlaid and each slot's date; without it, the bare pattern.
   */
  async getSlots(weekStart?: string): Promise<RotaSlot[]> {
    const raw = required(
      await this.get<RotaSlot[]>("/dashboard/rota/slots", weekStart ? { weekStart } : undefined),
      "the rota"
    );
    // slotDate is a date-only field, and DateReviver turns those into Dates — normalise it
    // back so callers can compare it to the "YYYY-MM-DD" strings the grid works in.
    return Array.isArray(raw)
      ? raw.map((s) => ({ ...s, slotDate: s.slotDate ? toDateOnly(s.slotDate) ?? null : null }))
      : [];
  }

  /**
   * FORT-371: rostered hours per staff member for the week (the Monday, "YYYY-MM-DD").
   * Totalled server-side so there is one definition of "hours" rather than the browser
   * re-deriving it — the booking screens are the cautionary tale for that.
   */
  async getWeeklyHours(weekStart: string): Promise<StaffWeeklyHours[]> {
    const raw = await this.get<StaffWeeklyHours[]>(
      "/dashboard/rota/hours",
      { weekStart },
      { showLoading: false }
    );
    return Array.isArray(raw) ? raw : [];
  }

  /** FORT-370: assign (or replace) cover for one occurrence. `coverDate` is "YYYY-MM-DD". */
  async coverSlot(slotId: number, coverDate: string, coveringStaffId: number): Promise<void> {
    await this.put<void>(`/dashboard/rota/slots/${slotId}/cover`, { coverDate, coveringStaffId });
  }

  /** Removing the cover IS the revert — the base pattern shows through again. */
  async removeCover(slotId: number, coverDate: string): Promise<void> {
    await this.delete<void>(
      `/dashboard/rota/slots/${slotId}/cover?coverDate=${encodeURIComponent(coverDate)}`
    );
  }

  /** The logged-in staff member's shifts for a date (Staff Daily shift bar). */
  async getMyToday(date: string): Promise<RotaSlot[]> {
    const raw = await this.get<RotaSlot[]>("/dashboard/rota/my-today", { date }, { showLoading: false });
    return Array.isArray(raw) ? raw : [];
  }

  async createSlot(payload: SaveRotaSlotPayload): Promise<RotaSlot> {
    return this.post<RotaSlot>("/dashboard/rota/slots", payload);
  }

  /** Also serves the Manage Staff reassign — that is just a save with a different staffId. */
  async updateSlot(slotId: number, payload: SaveRotaSlotPayload): Promise<RotaSlot> {
    return this.put<RotaSlot>(`/dashboard/rota/slots/${slotId}`, payload);
  }

  async deleteSlot(slotId: number): Promise<void> {
    await this.delete<void>(`/dashboard/rota/slots/${slotId}`);
  }

  async getRoster(): Promise<RosterStaffMember[]> {
    const raw = required(
      await this.get<RosterStaffMember[]>("/dashboard/roster", undefined, { showLoading: false }),
      "the staff roster"
    );
    return Array.isArray(raw) ? raw : [];
  }

  async createStaff(payload: CreateRosterStaffPayload): Promise<RosterStaffMember> {
    return this.post<RosterStaffMember>("/dashboard/roster", payload);
  }

  async updateStaff(staffId: number, payload: UpdateRosterStaffPayload): Promise<RosterStaffMember> {
    return this.put<RosterStaffMember>(`/dashboard/roster/${staffId}`, payload);
  }

  // FORT-328: Work Allocation — merged Task/Call/BizDev entries.
  async getWeekAllocations(weekStart: string): Promise<Allocation[]> {
    const raw = await this.get<Record<string, unknown>[]>("/dashboard/allocations", { weekStart });
    return Array.isArray(raw) ? raw.map(normalizeAllocation) : [];
  }

  /**
   * FORT-375: what the team finished in the week — Task, Call and BizDev alike, each with
   * who completed it and when.
   *
   * Guarded with required(): ClubBookingApiClient.get resolves to undefined on failure unless
   * showError is set, so without it a 500 would render as "nothing was completed this week" —
   * which is exactly the answer a manager would believe.
   */
  async getCompletedWork(weekStart: string): Promise<Allocation[]> {
    const raw = required(
      await this.get<Record<string, unknown>[]>("/dashboard/allocations/completed", { weekStart }),
      "completed work"
    );
    return Array.isArray(raw) ? raw.map(normalizeAllocation) : [];
  }

  /** The logged-in staff member's allocations for a date (Staff Daily "your work today"). */
  async getMyAllocationsToday(date: string): Promise<Allocation[]> {
    const raw = await this.get<Record<string, unknown>[]>(
      "/dashboard/allocations/my-today",
      { date },
      { showLoading: false }
    );
    return Array.isArray(raw) ? raw.map(normalizeAllocation) : [];
  }

  async createTaskAllocation(payload: CreateTaskAllocationPayload): Promise<void> {
    await this.post("/dashboard/allocations/task", payload);
  }

  async createBizDevAllocation(payload: CreateBizDevAllocationPayload): Promise<void> {
    await this.post("/dashboard/allocations/bizdev", payload);
  }

  async deleteTaskAllocation(taskId: number): Promise<void> {
    await this.delete<void>(`/dashboard/allocations/task/${taskId}`);
  }

  async deleteBizDevAllocation(allocationId: number): Promise<void> {
    await this.delete<void>(`/dashboard/allocations/bizdev/${allocationId}`);
  }

  /**
   * FORT-367: sign an assigned task off as done.
   *
   * Task allocations *are* dbo.staff_task rows (V57), so this is the long-standing
   * /dashboard/tasks/{id}/status endpoint. That write is assignee-scoped in SQL: a task
   * belonging to someone else matches no row and comes back 404, which is why the caller
   * must only offer this on the acting staff member's own work.
   */
  async completeTask(taskId: number): Promise<void> {
    await this.put<void>(`/dashboard/tasks/${taskId}/status`, { status: "Completed" }, {
      showSuccess: false,
      showError: false,
    });
  }

  /**
   * FORT-374: sign a Business Development allocation off as done.
   *
   * Assignee-scoped in SQL like completeTask above, and 404 also covers "already signed
   * off" — so the caller must only offer this on the acting staff member's own open work.
   * Notifications are suppressed for the same reason as completeTask: the caller renders
   * its own success/failure inline next to the row being signed off.
   */
  async signOffBizDevAllocation(allocationId: number): Promise<void> {
    await this.post<void>(`/dashboard/allocations/bizdev/${allocationId}/signoff`, {}, {
      showSuccess: false,
      showError: false,
    });
  }

  /**
   * FORT-374: undo a sign-off, putting the allocation back on the day.
   *
   * Same assignee scoping as signing off, so this only ever reverses your own work. It exists
   * because sign-off otherwise removes the row from every screen — the week grid and Staff
   * Daily both hide completed allocations — leaving a mis-click unrecoverable.
   */
  async reopenBizDevAllocation(allocationId: number): Promise<void> {
    await this.delete<void>(`/dashboard/allocations/bizdev/${allocationId}/signoff`, {
      showSuccess: false,
      showError: false,
    });
  }
}
